import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouteObject,
} from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';
import LaunchDarklyPageAccess from 'components/LaunchDarklyPageAccess/LaunchDarklyPageAccess';
import { RequireAuth } from 'lib/auth/RequireAuth';
import { verifyAuth } from 'lib/auth/auth';
import { RegistrationProvider } from 'lib/context/RegistrationContext/RegistrationContext';
import { ActivityFormWrapper } from 'domains/ActivityForm/ActivityFormWrapper';
import ConsolidatedCheckout from 'domains/ConsolidatedCheckout/ConsolidatedCheckout';
import { MemberRegistration } from 'domains/MemberRegistration/MemberRegistration';
import RegistrationAddOnsCard from 'domains/MemberRegistration/RegistrationAddOnsCard/RegistrationAddOnsCard';
import { RegistrationFileUpload } from 'domains/MemberRegistration/RegistrationFileUpload/RegistrationFileUpload';
import { RegistrationInfoCard } from 'domains/MemberRegistration/RegistrationInfoCard/RegistrationInfoCard';
import { RegistrationReviewCard } from 'domains/MemberRegistration/RegistrationReviewCard/RegistrationReviewCard';
import { RegistrationSuccessCard } from 'domains/MemberRegistration/RegistrationSuccessCard/RegistrationSuccessCard';
import { RegistrationWaiversCard } from 'domains/MemberRegistration/RegistrationWaiversCard/RegistrationWaiversCard';
import Membership from 'domains/Membership/Membership';
import MembershipSuccess from 'domains/Membership/MembershipSuccess';
import RosterManagement from 'domains/RosterManagement/RosterManagement';
import { RosterRollover } from 'domains/RosterManagement/RosterRollover/RosterRollover';
import { UpdateTeam } from 'domains/RosterManagement/UpdateTeam/UpdateTeam';
import ErrorPage from 'domains/Shell/ErrorPage/ErrorPage';
import IFrameContainer from 'domains/Shell/IFrameContainer/IFrameContainer';
import Layout from 'domains/Shell/Layout/Layout';
import SingleActivityView from 'domains/SingleActivityView/SingleActivityView';
import SiteLevelCalendar from 'domains/SiteLevelCalendar/SiteLevelCalendar';
import SwatchGenerator from 'domains/SwatchGenerator/SwatchGenerator';
import { Registration } from 'domains/Tournaments/Registration/Registration';
import { SelectRegistrationType } from 'domains/Tournaments/SelectRegistrationType/SelectRegistrationType';
import { TournamentDetailsPage } from 'domains/Tournaments/TournamentDetailsPage/TournamentDetailsPage';
import { TournamentParticipantsPage } from 'domains/Tournaments/TournamentParticipantsPage/TournamentParticipantsPage';
import EditMobileAppAccessWrapper from 'domains/YouthAthleteAccess/EditMobileAppAccessWrapper';

export default function useGetRoutePaths() {
  const { mobileYouthAthleteAccess, mpMembershipForm } = useFlags();
  const routeElements = (
    <>
      <Route path="/" element={<Layout />} errorElement={<ErrorPage />}>
        {/* Protected Paths will not render unless the user is authenticated. */}
        <Route
          path="checkout"
          element={<ConsolidatedCheckout pageName="checkout" />}
          errorElement={<ErrorPage />}
          loader={verifyAuth}
        />
        <Route
          path="cartSummary"
          element={<ConsolidatedCheckout pageName="checkout" />}
          errorElement={<ErrorPage />}
          loader={verifyAuth}
        />
        <Route
          path="confirmation"
          element={<ConsolidatedCheckout pageName="confirmation" />}
          errorElement={<ErrorPage />}
          loader={verifyAuth}
        />
        <Route
          element={
            <RequireAuth>
              <RegistrationProvider>
                <MemberRegistration />
              </RegistrationProvider>
            </RequireAuth>
          }
          errorElement={<ErrorPage />}
        >
          <Route path="app/registration" element={<RegistrationInfoCard />} />
          <Route
            path="app/registration/waivers"
            element={<RegistrationWaiversCard />}
          />
          <Route
            path="app/registration/upload-documents"
            element={<RegistrationFileUpload />}
          />
          <Route
            path="app/registration/review"
            element={<RegistrationReviewCard />}
          />
          <Route
            path="app/registration/add-ons"
            element={<RegistrationAddOnsCard />}
          />
          <Route
            path="app/registration/success"
            element={<RegistrationSuccessCard />}
          />
        </Route>

        <Route
          errorElement={<ErrorPage />}
          loader={verifyAuth}
          path="app/:programType/:programId/teams/:teamId"
        >
          <Route path="edit">
            <Route
              index
              element={
                <LaunchDarklyPageAccess featureFlagName="ngCustomTeamFormFieldRequirementBypass">
                  <RequireAuth>
                    <UpdateTeam />
                  </RequireAuth>
                </LaunchDarklyPageAccess>
              }
            />
          </Route>
          <Route path="roster">
            <Route index element={<RosterManagement />} />
            <Route
              path="rollover"
              element={
                <LaunchDarklyPageAccess featureFlagName="ngRosterRollover">
                  <RequireAuth>
                    <RosterRollover />
                  </RequireAuth>
                </LaunchDarklyPageAccess>
              }
            />
          </Route>
        </Route>
        <Route
          path="app/:programType/:programId/schedule/activities/:activityId"
          element={
            <RequireAuth>
              <SingleActivityView />
            </RequireAuth>
          }
          errorElement={<ErrorPage />}
        />
        {/* End Protected Paths */}

        {/* Public Paths */}
        {/* SwatchGenerator is here temporarily until it can be moved to
       Storybook -DC 4/6/23 */}
        <Route
          path="swatchGenerator"
          element={<SwatchGenerator />}
          errorElement={<ErrorPage />}
        />
        <Route
          path="app/tournaments/:tournamentId"
          element={<TournamentDetailsPage />}
          errorElement={<ErrorPage />}
        />
        <Route
          path="app/tournaments/participants/:tournamentId"
          element={<TournamentParticipantsPage />}
          errorElement={<ErrorPage />}
        />
        <Route index element={<IFrameContainer />} />
        <Route
          path="app/tournaments/:tournamentId/registration"
          element={
            <RequireAuth>
              <Registration />
            </RequireAuth>
          }
          errorElement={<ErrorPage />}
        />
        <Route
          path="app/tournaments/:tournamentId/select-registration-type"
          element={
            <RequireAuth>
              <SelectRegistrationType />
            </RequireAuth>
          }
          errorElement={<ErrorPage />}
        />
        {mpMembershipForm ? (
          <>
            <Route
              path="membership"
              element={
                <RequireAuth>
                  <Membership />
                </RequireAuth>
              }
              errorElement={<ErrorPage />}
            />
            <Route
              path="membershipSuccess"
              element={
                <RequireAuth>
                  <MembershipSuccess />
                </RequireAuth>
              }
              errorElement={<ErrorPage />}
            />
          </>
        ) : null}
        {mobileYouthAthleteAccess ? (
          <Route
            path="mobileAppAccess"
            element={
              <RequireAuth>
                <EditMobileAppAccessWrapper />
              </RequireAuth>
            }
            errorElement={<ErrorPage />}
          />
        ) : null}
        <Route path="*" element={<IFrameContainer />} />
        <Route
          path="app/calendar"
          element={<SiteLevelCalendar />}
          errorElement={<ErrorPage />}
        />
        <Route
          path="calendar"
          element={<SiteLevelCalendar />}
          errorElement={<ErrorPage />}
        />
        {/* End Public Paths */}
      </Route>

      <Route
        path="app/clubteams/:subProgramId/teams/:teamId/activity/:activityId?"
        element={
          <RequireAuth>
            <ActivityFormWrapper />
          </RequireAuth>
        }
        errorElement={<ErrorPage />}
      />
    </>
  );

  const routesConfig: RouteObject[] = createRoutesFromElements(routeElements);

  return createBrowserRouter(routesConfig);
}
